import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MotionConfig, motion } from 'framer-motion';
// Only global theme variables are left in these files; importing them here
// keeps their place in the cascade (other pages' styles still use them).
import './CustomerManagementPage.module.css';
import './EditCustomerModal.module.css';
import './ChangeCustomerState.module.css';
import './CustomerTariffsModal.module.css';
// Global dark-mode sheet that other pages rely on
import './CustomerManagementDarkMode.css';
import DataCardList from '../../components/data/DataCardList';
import DataTable from '../../components/data/DataTable';
import DataToolbar from '../../components/data/DataToolbar';
import FilterSheet from '../../components/data/FilterSheet';
import TablePagination from '../../components/data/TablePagination';
import { fadeUp, staggerContainer } from '../../components/ui/motionPresets';
import useElementWidth from '../../hooks/useElementWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import useTableColumns from '../../hooks/useTableColumns';
import useViewportFit from '../../hooks/useViewportFit';
import { useTranslation } from '../../i18n/LanguageContext';
import CustomerCell, { CustomerCardRow, getCellTitle } from './components/CustomerCell';
import CustomerDetailPane from './components/CustomerDetailPane';
import CustomerModals from './components/CustomerModals';
import { CustomerPageHeader, CustomerStats } from './components/CustomerOverview';
import { downloadCustomersExcel } from './customerActions';
import {
  COLUMN_KEYS,
  CUSTOMER_COLUMNS,
  DEFAULT_VISIBLE_COLUMNS,
  INDEX_COLUMN,
  INDEX_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  columnLabel,
  formatIsoDate,
  getFilterState,
  getFilterValueLabel,
  optionLabel,
} from './customerColumns';
import useCustomerDirectory from './useCustomerDirectory';

const COLUMNS_STORAGE_KEY = 'customerManagement.tableColumns.v1';
// How long a row stays highlighted after jumping to it (e.g. a new customer)
const FLASH_MS = 2000;
// From this width the customer panel sits beside the table instead of over it
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
// The table card ends this far above the bottom of the screen (Layout's main padding)
const FIT_BOTTOM_GAP = 24;
// Below this the card stops shrinking and the page scrolls instead
const FIT_MIN_HEIGHT = 400;
// Below this list width the table turns into a card list with fixed pages
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Müşteri Yönetimi (Tailwind), built like Çalışan Yönetimi: the table card
 * reaches at most the bottom of the screen and pages as many rows as fit
 * there; a short result ends right under its last row. The selected
 * customer's panel is docked beside the table on wide screens and a drawer
 * otherwise. Narrow screens get a card list and a filter sheet.
 */
const CustomerManagementPage = () => {
  const { t, language, locale } = useTranslation();
  const {
    allCustomers, setAllCustomers, filteredCustomers, filters, handleFilter, clearFilters, searchTerm, setSearchTerm,
    isFilterActive, isLoading, hasLoaded, statsData, pageRows, pageStart, currentPage, totalPages,
    pageSize, setPageSize, goToPage, reveal, refreshCustomers,
  } = useCustomerDirectory();
  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: CUSTOMER_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  const maxCardHeight = !compactList && fitHeight ? fitHeight : undefined;

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Which dialog is open: card | form | status | addresses | personnel
  const [modal, setModal] = useState(null);
  const [formMode, setFormMode] = useState('edit');
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [flashId, setFlashId] = useState(null);
  const [revealId, setRevealId] = useState(null);

  // The selection follows the list: refreshed data replaces it, and it is
  // dropped once the customer is no longer listed (never while a dialog about
  // that customer is open)
  useEffect(() => {
    if (!selectedCustomer || modal) return;
    const current = filteredCustomers.find((customer) => customer.id === selectedCustomer.id);
    if (!current) setSelectedCustomer(null);
    else if (current !== selectedCustomer) setSelectedCustomer(current);
  }, [filteredCustomers, selectedCustomer, modal]);

  // A customer just added: select it and show its page, if it is listed
  useEffect(() => {
    if (!revealId) return;
    setRevealId(null);
    const target = filteredCustomers.find((customer) => customer.id === revealId);
    if (!target) return;
    setSelectedCustomer(target);
    reveal(target.id);
    setFlashId(target.id);
    // After the page holding it has rendered
    setTimeout(() => {
      document.querySelector(`[data-row-id="${target.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  }, [revealId, filteredCustomers, reveal]);

  useEffect(() => {
    if (!flashId) return undefined;
    const timer = setTimeout(() => setFlashId(null), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flashId]);

  // Escape clears the selection, unless a dialog, field or popover has it
  useEffect(() => {
    if (!selectedCustomer) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || modal || showFilterSheet) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedCustomer(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCustomer, modal, showFilterSheet]);

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  const translatedOptions = useCallback((key) => (CUSTOMER_COLUMNS[key].options || []).map((option) => ({
    value: option.value,
    label: optionLabel(t, option),
  })), [t]);

  const tableColumns = useMemo(() => [
    { key: INDEX_COLUMN, label: '#', resizable: false },
    ...columns.visibleKeys.map((key) => ({
      key,
      label: columnLabel(t, key),
      filter: { type: CUSTOMER_COLUMNS[key].type, options: translatedOptions(key) },
    })),
  ], [columns.visibleKeys, t, translatedOptions]);

  const { getColumnWidth: getDataColumnWidth } = columns;
  const getColumnWidth = useCallback(
    (key) => (key === INDEX_COLUMN ? INDEX_COLUMN_WIDTH : getDataColumnWidth(key)),
    [getDataColumnWidth]
  );

  const filterChips = Object.keys(filters)
    .filter((key) => key !== 'Durum' && CUSTOMER_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => {
      const { type } = CUSTOMER_COLUMNS[key];
      let value = String(filters[key]);
      if (type === 'select') value = getFilterValueLabel(t, key, filters[key]);
      else if (type === 'date') value = formatIsoDate(filters[key], locale);
      return { key, label: columnLabel(t, key), value, state: getFilterState(key, filters[key]) };
    });

  const handleDownload = async () => {
    setIsExporting(true);
    try {
      // Ekranda kullanılan (eksiksiz) liste dışa aktarılır
      const customers = allCustomers.length > 0 ? allCustomers : await refreshCustomers();
      await downloadCustomersExcel({
        customers,
        columnKeys: columns.visibleKeys,
        messages: {
          preparing: t('customers.toast.excelPreparing'),
          success: t('customers.toast.excelSuccess'),
          error: t('customers.toast.excelError'),
        },
      });
    } finally {
      setIsExporting(false);
    }
  };

  // ↑/↓ in the panel: step through the list, turning pages as needed
  const selectedIndex = selectedCustomer ? filteredCustomers.findIndex((customer) => customer.id === selectedCustomer.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredCustomers[selectedIndex + offset];
    if (!sibling) return;
    setSelectedCustomer(sibling);
    reveal(sibling.id);
  };

  const openEdit = () => {
    setFormMode('edit');
    setModal('form');
  };

  const canClearFilters = Object.keys(filters).length > 0 || Boolean(searchTerm);

  const paneProps = {
    customer: selectedCustomer,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredCustomers.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredCustomers.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedCustomer(null),
    onOpenCard: () => setModal('card'),
    onEdit: openEdit,
    onOpenAddresses: () => setModal('addresses'),
    onOpenPersonnel: () => setModal('personnel'),
    onToggleStatus: () => setModal('status'),
  };

  return (
    <>
      <MotionConfig reducedMotion="user">
        <motion.div
          lang={language}
          variants={staggerContainer(0.06)}
          initial="hidden"
          animate="show"
          className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10"
        >
          <motion.div variants={fadeUp}>
            <CustomerPageHeader
              isExporting={isExporting}
              onDownload={handleDownload}
              onAdd={() => {
                setFormMode('add');
                setModal('form');
              }}
            />
          </motion.div>

          <motion.div variants={fadeUp}>
            <CustomerStats total={statsData.total} active={statsData.active} inactive={statsData.inactive} ready={hasLoaded} />
          </motion.div>

          <motion.div ref={tableAreaRef} variants={fadeUp} className="tw-flex tw-items-start tw-gap-4">
            <section ref={listCardRef} aria-label={t('customers.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
              <DataToolbar
                search={{
                  value: searchTerm,
                  onChange: setSearchTerm,
                  placeholder: t('customers.search.placeholder'),
                  placeholderShort: t('customers.search.placeholderShort'),
                  label: t('customers.search.label'),
                }}
                status={{
                  options: [
                    { value: 'aktif', label: t('customers.status.active') },
                    { value: 'inaktif', label: t('customers.status.inactive') },
                    { value: '', label: t('customers.status.all') },
                  ],
                  value: filters['Durum'] ? String(filters['Durum']) : '',
                  onChange: (value) => handleFilter('Durum', value),
                  layoutId: 'customer-status-filter',
                }}
                columnMenu={{
                  columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(t, key) })),
                  visibleColumns: columns.visibleColumns,
                  onToggle: columns.toggleColumn,
                  onShowAll: columns.showAllColumns,
                  onReset: columns.resetColumns,
                }}
                compact={compactList}
                onOpenFilters={() => setShowFilterSheet(true)}
                chips={filterChips}
                onRemoveChip={(key) => handleFilter(key, '')}
                onClearAll={clearFilters}
              />

              {compactList ? (
                <DataCardList
                  label={t('customers.listLabel')}
                  rows={pageRows}
                  renderCard={(customer) => <CustomerCardRow customer={customer} />}
                  selectedId={selectedCustomer?.id}
                  flashId={flashId}
                  onSelect={setSelectedCustomer}
                  isLoading={isLoading}
                  isBusy={isLoading && hasLoaded}
                  emptyLabel={t('customers.table.empty')}
                  canClearFilters={canClearFilters}
                  onClearFilters={clearFilters}
                />
              ) : (
                <DataTable
                  label={t('customers.listLabel')}
                  rows={pageRows}
                  columns={tableColumns}
                  getColumnWidth={getColumnWidth}
                  onResizeColumn={columns.setColumnWidth}
                  onResetColumnWidth={columns.resetColumnWidth}
                  pinnedKeys={PINNED_COLUMNS}
                  filters={filters}
                  onFilterChange={handleFilter}
                  getFilterState={getFilterState}
                  renderCell={(customer, key, index) => (key === INDEX_COLUMN
                    ? <span className="tw-text-xs tw-font-semibold tw-text-slate-400 tw-tabular-nums dark:tw-text-slate-500">{pageStart + index + 1}</span>
                    : <CustomerCell columnKey={key} customer={customer} />)}
                  getCellTitle={getCellTitle}
                  selectedId={selectedCustomer?.id}
                  flashId={flashId}
                  onSelect={setSelectedCustomer}
                  isLoading={isLoading}
                  isBusy={isLoading && hasLoaded}
                  skeletonRows={pageSize}
                  maxCardHeight={maxCardHeight}
                  onCapacityChange={setPageSize}
                  emptyLabel={t('customers.table.empty')}
                  canClearFilters={canClearFilters}
                  onClearFilters={clearFilters}
                  onShowColumns={columns.resetColumns}
                />
              )}

              <TablePagination
                rangeStart={pageStart + 1}
                rangeEnd={pageStart + pageRows.length}
                resultCount={filteredCustomers.length}
                rangeOf={t('customers.pagination.rangeOf', { total: filteredCustomers.length.toLocaleString(locale) })}
                ofTotal={isFilterActive ? t('customers.pagination.ofTotal', { total: statsData.total.toLocaleString(locale) }) : undefined}
                currentPage={currentPage}
                totalPages={totalPages}
                isLoading={isLoading && hasLoaded}
                compact={compactList}
                onPage={goToPage}
              />
            </section>

            {dockPanel && <CustomerDetailPane docked height={fitHeight} {...paneProps} />}
          </motion.div>
        </motion.div>

        {/* Below the docking width: a drawer over the page (portalled to <body>) */}
        {!dockPanel && <CustomerDetailPane {...paneProps} />}
      </MotionConfig>

      <CustomerModals
        modal={modal}
        formMode={formMode}
        customer={selectedCustomer}
        onClose={() => setModal(null)}
        onOpen={setModal}
        onEdit={openEdit}
        onSaved={async (newId) => {
          await refreshCustomers();
          if (newId) setRevealId(newId);
        }}
        onStatusChanged={(customerId, newStatus) => {
          // Shown at once; the reload then refreshes the head counts too
          setAllCustomers((prev) => prev.map((customer) => (customer.id === customerId ? { ...customer, Durum: newStatus } : customer)));
          refreshCustomers();
        }}
      />

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={COLUMN_KEYS.filter((key) => key !== 'Durum').map((key) => ({
          key,
          label: columnLabel(t, key),
          type: CUSTOMER_COLUMNS[key].type,
          options: translatedOptions(key),
        }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredCustomers.length}
      />
    </>
  );
};

export default CustomerManagementPage;
